import React, { Fragment, useCallback, useEffect, useState } from "react";
import UserInfoSummary from "./UserInfoSummary";
import UserEmailsTable from "./UserEmailsTable";
import UserAliasesTable from "./UserAliasesTable";
import ViewSection from "./ViewSection";
import {
    faClock,
    faEnvelope,
    faIdCardAlt,
    faInfoCircle,
    faKey,
    faSignIn,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import UserRoleSummary from "./UserRoleSummary";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFetch } from "./FetchProvider";
import UserSessionTable from "./user_session/UserSessionTable";
import user from "../descriptors/user";
import { TitledContent } from "./TitledContent";
import { Button, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import ResourceFormButton from "./ResourceFormButton";
import AddRoleButton from "./AddRoleButton";
import NotSetValue from "./NotSetValue";

const baseApiUrl = "/api/user/totp";

const TOtpSettings = ({ id }: { id: number }) => {
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const fetch = useFetch();

    const clear = async () => {
        const response = await fetch(baseApiUrl + `/disable/${id}`);
        if (response.status === 200 && response.status < 500) setEnabled(false);
        else alert("Unexpected error");
    };

    const fetchData = useCallback(async () => {
        const response = await fetch(baseApiUrl + `/info/${id}`);
        if (response.status !== 200) {
            alert("Unexpected error");
            return;
        }

        const data = await response.json();

        setEnabled(data.enabled);
    }, [fetch, id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (enabled === null) return <div>...</div>;

    const DisableButton = () => (
        <Button onClick={(_) => clear()}>
            <FormattedMessage id="account.totp.disable_button" />
        </Button>
    );

    const StateLabel = () => (
        <span style={{ marginRight: 15 }}>
            <FormattedMessage id={"account.totp." + (enabled ? "enabled" : "disabled")} />
        </span>
    );

    return (
        <div>
            <StateLabel /> {enabled ? <DisableButton /> : null}
        </div>
    );
};

const UserView = () => {
    const renderers = user.renderers?.read;
    let revalidator = useRevalidator();
    const intl = useIntl();
    const resourceName = "user";
    const data = useLoaderData() as any;
    const fieldNameKeyFactory = (fieldName: string) => `${resourceName}.${fieldName}`;

    let name: any = data.name;
    if (!name) {
        if (data.aliases.length) name = data.aliases[0].name;
        else name = <NotSetValue label="user.no_name" />;
    }

    let basicDataRows = [];

    for (let name of ["given_name", "family_name", "gender", "birthdate", "locale"]) {
        let value = data[name];
        if (name in renderers) value = renderers[name](value, data);
        let nameKey = fieldNameKeyFactory(name);
        basicDataRows.push(
            <Fragment key={name}>
                <Grid size={4}>
                    <FormattedMessage id={nameKey} />
                </Grid>
                <Grid size={8}>{value}</Grid>
            </Fragment>,
        );
    }

    basicDataRows.push(
        <Fragment key="totp">
            <Grid size={4}>
                <FormattedMessage id="account.totp.settings_short" />
            </Grid>
            <Grid size={8}>
                <TOtpSettings id={data.id} />
            </Grid>
        </Fragment>,
    );

    return (
        <>
            <TitledContent title={name}>
                <Typography variant="subtitle1">
                    <span>
                        <FormattedMessage id={"user." + data.gender} />
                    </span>
                    , {data.birthdate || <FormattedMessage id="user.no_birth_date" />}
                </Typography>
                <Stack spacing={5} mt={5}>
                    <ViewSection
                        icon={faUser}
                        title="user.basic_data"
                        button={
                            <ResourceFormButton
                                resourceName="managed_user"
                                action="update"
                                id={data.id}
                                buttonType="section-icon"
                            />
                        }
                    >
                        <Grid container spacing={2}>
                            {...basicDataRows}
                        </Grid>
                    </ViewSection>
                    <ViewSection icon={faIdCardAlt} title="user.oidc_sub">
                        <div>{data["user_sub"]["user_sub"]}</div>
                    </ViewSection>
                    <ViewSection
                        icon={faSignIn}
                        title="user.aliases"
                        button={
                            <ResourceFormButton
                                resourceName="user_alias"
                                action="create"
                                buttonType="section-icon"
                                extraRequestData={{
                                    user: data.id,
                                    owner_id: data.id,
                                    owner_type: "USER",
                                }}
                            />
                        }
                    >
                        <UserAliasesTable rows={data.aliases} showOwnerType={true} />
                    </ViewSection>
                    <ViewSection icon={faInfoCircle} title="user.user_infos">
                        <UserInfoSummary userInfos={data["user_sub"]["user_infos"]} />
                    </ViewSection>
                    <ViewSection
                        icon={faKey}
                        title="user.roles"
                        button={<AddRoleButton userSubId={data["user_sub"].id} />}
                    >
                        <UserRoleSummary
                            userGrants={data.user_sub["user_grants"]}
                            userSubId={data.user_sub.id}
                            onUpdateRole={(changed) => {
                                if (changed) revalidator.revalidate();
                            }}
                        />
                    </ViewSection>
                    <ViewSection
                        icon={faEnvelope}
                        title="user.emails"
                        button={
                            <ResourceFormButton
                                resourceName="user_email"
                                action="create"
                                buttonType="section-icon"
                                extraRequestData={{ user: data.id }}
                            />
                        }
                    >
                        <UserEmailsTable rows={data.emails} />
                    </ViewSection>
                    <ViewSection
                        icon={faClock}
                        title="user.sessions"
                        button={
                            <ResourceFormButton
                                resourceName="token"
                                action="create"
                                buttonType="section-icon"
                                extraRequestData={{
                                    refresh_token_expires_in: 0,
                                    user_sub: data["user_sub"]["user_sub"],
                                }}
                            />
                        }
                    >
                        <UserSessionTable userSub={data["user_sub"]["user_sub"]} />
                    </ViewSection>
                </Stack>
            </TitledContent>
        </>
    );
};

export default UserView;
